import React, { useMemo } from 'react';
import _ from 'lodash';
import { Theme } from '@mui/material/styles';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Fields } from './Fields';
import { vendorsLogoByType } from '../../../../../constants';
import { ProductPrice } from '../../../cardPriceBlock/ProductPrice';
import { ProposalCardContainer } from '../cardContainer/ProposalCardContainer';
import {
  AftermarketProductTypes,
  MarkupType,
  ProposalProductCardByVendorProps,
  ProposalProductCardModes,
} from '../../../../../types';
import { BuckeyeDeductible, BuckeyePlan, BuckeyeSurcharge, BuckeyeTerm } from './types';

const useStyles = makeStyles<Theme>(({ palette }) => ({
  surchargesGrid: {
    gap: '8px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  surchargeCard: {
    gap: '4px',
    display: 'flex',
    padding: '6px 8px',
    flexDirection: 'column',
    backgroundColor: palette.common.white,
    border: `1px solid ${palette.divider}`,
    borderRadius: '4px',
  },
}));

export const Card = ({
  mode,
  product,
  products,
  equipmentCost,
  prisingConfig,
  type = 'simple',
  productConfiguration,
  updateProductConfiguration,
}: ProposalProductCardByVendorProps) => {
  const vendorApiData = useMemo<{ plans: BuckeyePlan[] | null } | undefined>(() => {
    return product?.aftermarketProduct?.criteria?.[0]?.event?.params?.AFTERMARKET_PRODUCT_VENDOR_API_DATA
      ?.API_RESPONSE_DATA;
  }, [product]);

  prisingConfig.isShowRate = false;

  const logo = vendorsLogoByType[AftermarketProductTypes.TruckMasterBuckeye];

  const selectedTerm: BuckeyeTerm | null = productConfiguration?.coverage ?? null;
  const selectedDeductible: BuckeyeDeductible | null =
    selectedTerm?.deductibles.find(d => d.deductibleId === productConfiguration?.buckeyeDeductibleId) ?? null;
  const addons: any[] = productConfiguration?.addons ?? [];

  const calculateCosts = (
    term: BuckeyeTerm | null,
    deductible: BuckeyeDeductible | null,
    surcharges: BuckeyeSurcharge[]
  ) => {
    if (!term) return { cost: 0, retailCost: 0 };

    const basePricing =
      deductible && deductible.pricing && deductible.pricing.retailAmount !== 0 ? deductible.pricing : term.pricing;
    const surchargesRemit = surcharges.reduce((sum, s) => sum + s.pricing.remitAmount, 0);
    const surchargesRetail = surcharges.reduce((sum, s) => sum + s.pricing.retailAmount, 0);

    return {
      cost: basePricing.remitAmount + surchargesRemit,
      retailCost: basePricing.retailAmount + surchargesRetail,
    };
  };

  const applyMarkup = (baseCost: number, baseRetailCost: number): number => {
    const markupToUse = productConfiguration?.markup;
    if (!markupToUse || markupToUse.markup === 0) return baseRetailCost;

    if (markupToUse.type === MarkupType.Percentage) {
      return _.round(baseCost * (1 + markupToUse.markup / 100), 2);
    }
    return baseCost + markupToUse.markup;
  };

  useMemo(() => {
    if (!selectedTerm) return;

    const needToUpdateProductConfiguration =
      mode !== ProposalProductCardModes.Constructor &&
      (productConfiguration.cost === 0 || productConfiguration.retailCost === 0);

    if (!needToUpdateProductConfiguration) return;

    const currentAddons: any[] = productConfiguration?.addons ?? [];

    const currentSurcharges = currentAddons
      .map((addon: any) => selectedTerm.surcharges.find(s => s.surchargeId === addon.thirdPartyId))
      .filter(Boolean) as BuckeyeSurcharge[];

    const { cost, retailCost } = calculateCosts(selectedTerm, selectedDeductible, currentSurcharges);

    if (cost === 0 && retailCost === 0) return;

    const markupToUse = productConfiguration.markup ?? { type: MarkupType.Percentage, markup: 0 };
    let finalRetailCost = retailCost;
    if (markupToUse.markup !== 0) {
      finalRetailCost =
        markupToUse.type === MarkupType.Percentage
          ? _.round(cost * (1 + markupToUse.markup / 100), 2)
          : cost + markupToUse.markup;
    }

    updateProductConfiguration({
      cost,
      addons: currentAddons,
      retailCost: finalRetailCost,
      markup: markupToUse,
    });
  }, [mode, productConfiguration, selectedTerm, selectedDeductible, updateProductConfiguration]);

  const handlePlanChange = (index: number) => {
    const subName = (vendorApiData?.plans ?? [])[index]?.name ?? null;
    updateProductConfiguration({
      buckeyePlanIndex: index,
      subName,
      coverage: null,
      deductible: null,
      buckeyeDeductibleId: null,
      addons: [],
      cost: 0,
      retailCost: 0,
    });
  };

  const handleTermChange = (term: BuckeyeTerm) => {
    const autoDeductible = term.deductibles.length === 1 ? term.deductibles[0] : null;
    const { cost, retailCost } = calculateCosts(term, autoDeductible, []);
    const markupToUse = productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 };

    updateProductConfiguration({
      coverage: term,
      deductible: autoDeductible?.deductibleId.replace(/\D/g, '') ?? null,
      buckeyeDeductibleId: autoDeductible?.deductibleId ?? null,
      addons: [],
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: markupToUse,
    });
  };

  const handleDeductibleChange = (deductibleId: string) => {
    if (!selectedTerm) return;
    const deductible = selectedTerm.deductibles.find(d => d.deductibleId === deductibleId) ?? null;
    const currentSurcharges: BuckeyeSurcharge[] = addons
      .map((addon: any) => selectedTerm.surcharges.find(s => s.surchargeId === addon.thirdPartyId))
      .filter(Boolean) as BuckeyeSurcharge[];

    const { cost, retailCost } = calculateCosts(selectedTerm, deductible, currentSurcharges);
    const markupToUse = productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 };

    updateProductConfiguration({
      deductible: deductibleId?.replace(/\D/g, '') ?? null,
      buckeyeDeductibleId: deductibleId,
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: markupToUse,
    });
  };

  const handleSurchargeChange = (surchargeId: string, checked: boolean) => {
    if (!selectedTerm) return;

    const surcharge = selectedTerm.surcharges.find(s => s.surchargeId === surchargeId);
    if (!surcharge) return;

    let nextAddons = [...addons];
    if (checked) {
      nextAddons = [
        ...nextAddons,
        {
          thirdPartyId: surcharge.surchargeId,
          title: surcharge.description,
          cost: surcharge.pricing.remitAmount,
          retailCost: surcharge.pricing.retailAmount,
          category: 'Surcharge',
        },
      ];
    } else {
      nextAddons = nextAddons.filter((addon: any) => addon.thirdPartyId !== surchargeId);
    }

    const currentSurcharges = nextAddons
      .map((addon: any) => selectedTerm.surcharges.find(s => s.surchargeId === addon.thirdPartyId))
      .filter(Boolean) as BuckeyeSurcharge[];

    const { cost, retailCost } = calculateCosts(selectedTerm, selectedDeductible, currentSurcharges);
    const markupToUse = productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 };

    updateProductConfiguration({
      addons: nextAddons,
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: markupToUse,
    });
  };

  const classes = useStyles();

  const isPricingEditable =
    !vendorApiData?.plans ||
    (productConfiguration.buckeyePlanIndex != null &&
      selectedTerm !== null &&
      (selectedTerm.deductibles.length === 0 || productConfiguration.buckeyeDeductibleId != null));

  if (vendorApiData === undefined) return null;

  const displayProduct = productConfiguration?.subName
    ? { ...product, title: `${product.title} ${productConfiguration.subName}` }
    : product;

  return (
    <ProposalCardContainer
      mode={mode}
      type={type}
      logo={logo}
      factsSkipped={[]}
      proposalProduct={displayProduct}
      prisingConfig={prisingConfig}
      equipmentCost={equipmentCost}
      isPricingEditable={isPricingEditable}
      productConfiguration={productConfiguration}
      updateProductConfiguration={updateProductConfiguration}
    >
      {vendorApiData.plans && (
        <Fields
          plans={vendorApiData.plans}
          configuration={productConfiguration}
          handlePlanChange={handlePlanChange}
          handleTermChange={handleTermChange}
          handleDeductibleChange={handleDeductibleChange}
        />
      )}

      {selectedTerm && selectedTerm.surcharges.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Surcharges
          </Typography>
          <Box className={classes.surchargesGrid}>
            {selectedTerm.surcharges.map(surcharge => {
              const checked = addons.some((addon: any) => addon.thirdPartyId === surcharge.surchargeId);
              return (
                <Box key={surcharge.surchargeId} className={classes.surchargeCard}>
                  <FormControlLabel
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }, m: 0 }}
                    label={
                      <ProductPrice prefix={`${surcharge.description}: `} value={surcharge.pricing.retailAmount} />
                    }
                    control={
                      <Checkbox
                        size="small"
                        disableRipple
                        color="primary"
                        checked={checked}
                        onChange={e => handleSurchargeChange(surcharge.surchargeId, e.target.checked)}
                      />
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </ProposalCardContainer>
  );
};
